import { Fragment, useCallback, useEffect, useState } from "react";
import { ChevronRightIcon, LandmarkIcon, RefreshCwIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { BankAccountTransactionsTable } from "@/components/BankAccountTransactionsTable";
import { formatDateTime } from "@/lib/format";
import type { ProblemDetails } from "@/lib/problemDetails";

type ConnectionRow = components["schemas"]["BankConnectionAdminResponse"];
type BankConnectionState = components["schemas"]["BankConnectionState"];

const stateVariant: Record<BankConnectionState, "default" | "secondary" | "destructive" | "outline"> = {
  Initiated: "outline",
  AwaitingAuthorisation: "secondary",
  AwaitingFurtherAuthorisation: "secondary",
  Connected: "default",
  Failed: "destructive",
  Expired: "secondary",
  Reauthorising: "secondary",
  Revoked: "destructive",
  Disconnected: "outline",
};

// Terminal: nothing further happens to these, so they drop to the attempts table.
const CLOSED = new Set<BankConnectionState>(["Failed", "Revoked", "Disconnected"]);

/** Decimals cross the wire as strings on some routes and numbers on others, so both are handled. */
function formatMoney(amount: string | number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined) return "-";
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return "-";
  return `${value.toFixed(2)} ${currency ?? ""}`.trim();
}

/** Read-only, via the admin route, so staff see them whether or not the client holds banking. */
export function OrganisationBankConnectionsSection({ organisationId }: { organisationId: string }) {
  const [rows, setRows] = useState<ConnectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<ProblemDetails | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    const { data, error } = await api.GET("/admin/organisations/{organisationId}/bank-connections", {
      params: { path: { organisationId } },
    });
    if (error) {
      setProblem(error as ProblemDetails);
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, [organisationId]);

  useEffect(() => {
    load();
  }, [load]);

  const live = rows.filter((r) => !CLOSED.has(r.connection.state));
  const closed = rows.filter((r) => CLOSED.has(r.connection.state));

  if (loading && rows.length === 0 && !problem) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-5" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCwIcon className="size-4" />
          Refresh
        </Button>
      </div>

      <ProblemDetailsAlert problem={problem} fallback="Failed to load bank connections." />

      {!problem && rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-sm text-muted-foreground">
          <LandmarkIcon className="size-5" />
          No bank connections for this organisation.
        </div>
      ) : null}

      {live.map((row) => (
        <ConnectionCard key={row.connection.id} row={row} organisationId={organisationId} />
      ))}

      {closed.length > 0 ? <ClosedAttemptsTable rows={closed} organisationId={organisationId} /> : null}
    </div>
  );
}

/**
 * Every retry is a new connection, so an organisation can pile up failed and abandoned attempts. They collapse to
 * one row each; the full card opens on click.
 */
function ClosedAttemptsTable({ rows, organisationId }: { rows: ConnectionRow[]; organisationId: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Closed attempts ({rows.length})
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6" />
            <TableHead>Created</TableHead>
            <TableHead>Institution</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Provider code</TableHead>
            <TableHead>Provider message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const c = row.connection;
            const open = openId === c.id;
            return (
              <Fragment key={c.id}>
                <TableRow
                  className="cursor-pointer"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : c.id)}
                >
                  <TableCell>
                    <ChevronRightIcon
                      className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDateTime(c.createdAt)}</TableCell>
                  <TableCell className="font-medium">{c.institutionName}</TableCell>
                  <TableCell>
                    <Badge variant={stateVariant[c.state]}>{c.state}</Badge>
                  </TableCell>
                  <TableCell>{c.failureReason ?? "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{c.providerCode ?? "-"}</TableCell>
                  <TableCell className="max-w-80 truncate text-xs text-muted-foreground">
                    {c.providerMessage ?? "-"}
                  </TableCell>
                </TableRow>
                {open ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7} className="p-2">
                      <ConnectionCard row={row} organisationId={organisationId} />
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ConnectionCard({ row, organisationId }: { row: ConnectionRow; organisationId: string }) {
  const c = row.connection;
  const [openAccountId, setOpenAccountId] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-2 space-y-0">
        <CardTitle className="text-base">{c.institutionName}</CardTitle>
        <Badge variant={stateVariant[c.state]}>{c.state}</Badge>
        <Badge variant="outline">{row.clientType}</Badge>
        <span className="ml-auto flex items-center gap-1 font-mono text-xs text-muted-foreground">
          {c.id}
          <CopyButton value={c.id} />
        </span>
      </CardHeader>
      <CardContent className="space-y-5">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
          <Field label="Institution id" mono>{c.institutionId}</Field>
          <Field label="Yapily consent" mono>{row.yapilyConsentId ?? "-"}</Field>
          <Field label="Consent token stored">{row.hasStoredConsentToken ? "Yes" : "No"}</Field>
          <Field label="Consecutive 403s">{row.consecutiveAccessDenied}</Field>
          <Field label="Created">{formatDateTime(c.createdAt)}</Field>
          <Field label="Connected">{c.connectedAt ? formatDateTime(c.connectedAt) : "-"}</Field>
          <Field label="Expires">{c.expiresAt ? formatDateTime(c.expiresAt) : "-"}</Field>
          <Field label="Reconfirm by">{c.reconfirmBy ? formatDateTime(c.reconfirmBy) : "-"}</Field>
          <Field label="Last confirmed">{c.lastConfirmedAt ? formatDateTime(c.lastConfirmedAt) : "-"}</Field>
          <Field label="Reminder sent for">
            {row.reconfirmationDueNotifiedFor ? formatDateTime(row.reconfirmationDueNotifiedFor) : "-"}
          </Field>
          <Field label="Failure reason">{c.failureReason ?? "-"}</Field>
          <Field label="Provider code" mono>{c.providerCode ?? "-"}</Field>
        </dl>
        {c.providerMessage ? (
          <p className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">{c.providerMessage}</p>
        ) : null}

        <div>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Accounts</h3>
          {c.accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounts read through this connection.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6" />
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Sort code / account</TableHead>
                  <TableHead>IBAN</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Balance type</TableHead>
                  <TableHead>As of</TableHead>
                  <TableHead>Transactions synced</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.accounts.map((a) => {
                  const open = openAccountId === a.id;
                  return (
                    <Fragment key={a.id}>
                      <TableRow
                        className="cursor-pointer"
                        aria-expanded={open}
                        onClick={() => setOpenAccountId(open ? null : a.id)}
                      >
                        <TableCell>
                          <ChevronRightIcon
                            className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{a.name}</TableCell>
                        <TableCell>{a.type ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {a.sortCode || a.accountNumber ? `${a.sortCode ?? ""} ${a.accountNumber ?? ""}`.trim() : "-"}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{a.iban ?? "-"}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMoney(a.balance, a.currency)}</TableCell>
                        <TableCell className="font-mono text-xs">{a.balanceType ?? "-"}</TableCell>
                        <TableCell>{a.balanceAsOf ? formatDateTime(a.balanceAsOf) : "-"}</TableCell>
                        <TableCell>{a.transactionsSyncedAt ? formatDateTime(a.transactionsSyncedAt) : "-"}</TableCell>
                      </TableRow>
                      {open ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={9} className="p-2">
                            <BankAccountTransactionsTable organisationId={organisationId} bankAccountId={a.id} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">State history</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>At</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Provider code</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {row.events.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{formatDateTime(e.at)}</TableCell>
                  <TableCell>{e.fromState ?? "-"}</TableCell>
                  <TableCell>
                    <Badge variant={stateVariant[e.toState]}>{e.toState}</Badge>
                  </TableCell>
                  <TableCell>{e.reason ?? "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{e.providerCode ?? "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "truncate font-mono text-xs" : "truncate"}>{children}</dd>
    </div>
  );
}
